import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useOutletContext, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  pointerWithin,
  PointerSensor,
  MeasuringStrategy,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  motion, AnimatePresence, useMotionValue, useVelocity, useTransform, useSpring, useReducedMotion,
} from 'framer-motion';
import { ChevronDown, Sparkles, Archive, Check, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskCard, { TarjetaFlotante } from './board/TaskCard';
import QuickAddTask from './board/QuickAddTask';
import BoardFilters, { applyFilters } from './board/BoardFilters';
import TaskSlideOver from './TaskSlideOver';
import { useAuth } from '../../context/AuthContext';
import { CATEGORIAS } from './utils/constants';
import { refTareaUrl, tareaPorRefUrl } from './utils/helpers';
import {
  RESORTE_HUECO, RESORTE_COLUMNA, ESCALA_ALZADA, NumeroRodante,
} from './utils/motion';

// --- Destino del arrastre: la zona (sub-estado) que esta bajo el puntero. El
//     orden dentro de una zona no lo elige el usuario (va por fecha de
//     creacion), asi que las tarjetas no son destinos: solo las zonas. Si el
//     puntero queda fuera de toda zona (cabecera o pie de una columna, el
//     espacio entre columnas), cuenta la zona mas cercana: cruzar de una columna
//     a otra no hace parpadear el destino.
const distanciaARect = (p, r) => {
  const dx = p.x < r.left ? r.left - p.x : p.x > r.right ? p.x - r.right : 0;
  const dy = p.y < r.top ? r.top - p.y : p.y > r.bottom ? p.y - r.bottom : 0;
  return Math.hypot(dx, dy);
};

const zonaBajoPuntero = (args) => {
  const zonas = args.droppableContainers.filter((c) => c.data.current?.tipo === 'estado');
  const dentro = pointerWithin({ ...args, droppableContainers: zonas });
  if (dentro.length > 0) return dentro;
  const p = args.pointerCoordinates;
  if (!p) return [];
  let mejor = null;
  zonas.forEach((z) => {
    const r = args.droppableRects.get(z.id);
    if (!r) return;
    const d = distanciaARect(p, r);
    if (!mejor || d < mejor.d) mejor = { z, d };
  });
  return mejor ? [{ id: mejor.z.id, data: { droppableContainer: mejor.z, value: mejor.d } }] : [];
};

// Las zonas cambian de alto mientras se arrastra (se abre la ranura, se cierra
// el hueco): se vuelven a medir cada poco para que el destino no quede viejo.
const MEDICION = { droppable: { strategy: MeasuringStrategy.WhileDragging, frequency: 120 } };

// Clave de zona de una tarea: el sub-estado real, o la categoria en la vista general.
const zonaDeTarea = (t, esVistaGeneral) => (esVistaGeneral ? `cat:${t.estado?.categoria}` : t.estado_id);

// --- Ranura: el lugar exacto donde va a caer la tarjeta en la zona de destino
//     (por fecha de creacion), abierta con su mismo alto. Al soltar, la tarjeta
//     ocupa la ranura y nada alrededor se mueve.
const Ranura = ({ abierta, alto, color, reduce }) => (
  <motion.div
    initial={false}
    animate={{
      height: abierta ? alto : 0,
      marginBottom: abierta ? 6 : 0,
      borderWidth: abierta ? 1.5 : 0,
      opacity: abierta ? 1 : 0,
    }}
    transition={reduce ? { duration: 0 } : RESORTE_HUECO}
    className="rounded-lg border-dashed"
    style={{ borderColor: `${color}8c`, backgroundColor: `${color}12` }}
  />
);

// --- Droppable wrapper para un sub-estado ---

const SubEstadoDropZone = ({
  estado, tareas, color, onCardClick, proyectoColor, recienCompletadas, seleccionados,
  onToggleSelect, hasAnySelection, arrastre, zonaDestino, aterrizaje, versiones, entradas, reduce,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `estado-${estado.id}`,
    data: { tipo: 'estado', estado },
  });

  const esOrigen = !!arrastre && arrastre.zonaOrigen === estado.id;
  const destacada = isOver && !!arrastre && !esOrigen;

  // Donde caeria la tarjeta arrastrada en esta zona (mismo orden que el board).
  const indiceRanura = useMemo(() => {
    if (!arrastre || esOrigen) return -1;
    const t0 = new Date(arrastre.tarea.created_at).getTime();
    return tareas.filter((t) => new Date(t.created_at).getTime() < t0).length;
  }, [arrastre, esOrigen, tareas]);

  const ranura = indiceRanura >= 0 && (
    <Ranura key="ranura" abierta={destacada} alto={arrastre.rect?.height || 72} color={color} reduce={reduce} />
  );
  const huecoColapsado = zonaDestino != null && zonaDestino !== arrastre?.zonaOrigen;

  return (
    <div
      ref={setNodeRef}
      className="min-h-[72px] rounded-lg p-1 -m-1 transition-[background-color,box-shadow] duration-200"
      style={destacada ? {
        backgroundColor: `${color}0d`,
        boxShadow: `inset 0 0 0 1.5px ${color}59`,
      } : undefined}
    >
      <div className="flex flex-col">
        {tareas.map((t, i) => (
          <React.Fragment key={t.id}>
            {i === indiceRanura && ranura}
            <TaskCard
              tarea={t}
              onClick={onCardClick}
              // En la vista general cada card lleva el color de su propio
              // proyecto; dentro de un proyecto es el mismo color de siempre.
              proyectoColor={t.proyecto_color || proyectoColor}
              justCompleted={recienCompletadas?.has(t.id)}
              selected={seleccionados?.has(t.id)}
              onToggleSelect={onToggleSelect}
              hasAnySelection={hasAnySelection}
              layoutKey={versiones[t.id] || 0}
              entrada={entradas.get(t.id)}
              aterrizaje={aterrizaje?.id === t.id ? aterrizaje : null}
              altoHueco={arrastre?.rect?.height || 72}
              huecoColapsado={huecoColapsado}
              reduce={reduce}
            />
          </React.Fragment>
        ))}
        {indiceRanura === tareas.length && ranura}
      </div>
    </div>
  );
};

// --- Una columna (categoria) ---
//
// Plegar y desplegar anima el ancho (no un transform: las zonas de drop
// necesitan medidas reales) y funde un contenido en el otro. El contenido
// desplegado conserva sus 280px mientras se cierra, asi se recorta en vez de
// reacomodarse.

const ANCHO_PLEGADA = 44;
const ANCHO_COLUMNA = 280;

const Column = ({
  categoria,
  estados,
  tareasPorEstado,
  collapsed,
  onToggleCollapse,
  onCardClick,
  onCreate,
  proyectoColor,
  recienCompletadas,
  seleccionados,
  onToggleSelect,
  hasAnySelection,
  arrastre,
  zonaDestino,
  aterrizaje,
  versiones,
  entradas,
  reduce,
}) => {
  const totalTareas = estados.reduce((sum, e) => sum + (tareasPorEstado[e.id]?.length || 0), 0);
  const hasMultipleSubEstados = estados.length > 1;
  const primerEstado = estados[0];

  // Tint sutil del color de la categoría (~6% alpha). Da diferencia visual entre columnas
  // sin saturar como ClickUp. Aplicado al wrapper completo + redondeado.
  const tintBg = `${categoria.color}10`;
  const fundido = { duration: reduce ? 0 : 0.18 };

  return (
    <motion.div
      initial={false}
      animate={{ width: collapsed ? ANCHO_PLEGADA : ANCHO_COLUMNA }}
      transition={reduce ? { duration: 0 } : RESORTE_COLUMNA}
      className="relative flex-shrink-0 h-full rounded-xl overflow-hidden"
      style={{ backgroundColor: tintBg }}
    >
      <AnimatePresence initial={false}>
        {collapsed ? (
          <motion.div
            key="plegada"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fundido}
            className="absolute inset-y-0 left-0 flex flex-col"
            style={{ width: ANCHO_PLEGADA }}
          >
            <button
              onClick={onToggleCollapse}
              title={`Expandir ${categoria.label}`}
              className="flex flex-col items-center gap-2 px-2 py-3 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-black/[0.03] transition-colors"
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: categoria.color }}
              />
              <span
                className="text-[11px] font-bold tracking-[0.1em] uppercase whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                {categoria.label}
              </span>
              <NumeroRodante valor={totalTareas} className="text-[10px] font-medium text-slate-400" />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="abierta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fundido}
            className="absolute inset-y-0 left-0 flex flex-col pb-2"
            style={{ width: ANCHO_COLUMNA }}
          >
            {/* Header de columna */}
            <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0">
              <button
                onClick={onToggleCollapse}
                className="flex items-center gap-1.5 text-slate-700 hover:text-slate-900 transition-colors group"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: categoria.color }}
                />
                <span className="text-[11px] font-bold tracking-[0.1em] uppercase">
                  {categoria.label}
                </span>
                <NumeroRodante valor={totalTareas} className="text-[10px] font-medium text-slate-400 ml-0.5" />
                <ChevronDown
                  size={12}
                  strokeWidth={2.5}
                  className="text-slate-300 group-hover:text-slate-500"
                />
              </button>
            </div>

            {/* Contenido de columna (scroll interno cuando hay muchas tareas) */}
            <motion.div layoutScroll className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-2">
              {estados.map((estado) => {
                const tareas = tareasPorEstado[estado.id] || [];
                const empty = tareas.length === 0;
                return (
                  <div key={estado.id}>
                    {hasMultipleSubEstados && (
                      <div className="flex items-center gap-1.5 px-1 mb-1.5">
                        <span className={`text-[10px] font-medium tracking-wide ${empty ? 'text-slate-300' : 'text-slate-500'}`}>
                          {estado.nombre}
                        </span>
                        <NumeroRodante valor={tareas.length} className="text-[10px] text-slate-300" />
                        {!empty && <div className="flex-1 h-px bg-slate-200/60" />}
                      </div>
                    )}
                    <SubEstadoDropZone
                      estado={estado}
                      tareas={tareas}
                      color={categoria.color}
                      onCardClick={onCardClick}
                      proyectoColor={proyectoColor}
                      recienCompletadas={recienCompletadas}
                      seleccionados={seleccionados}
                      onToggleSelect={onToggleSelect}
                      hasAnySelection={hasAnySelection}
                      arrastre={arrastre}
                      zonaDestino={zonaDestino}
                      aterrizaje={aterrizaje}
                      versiones={versiones}
                      entradas={entradas}
                      reduce={reduce}
                    />
                  </div>
                );
              })}
            </motion.div>

            {/* QuickAdd fijo al pie de la columna. Sin onCreate (vista general) no se
                muestra: una tarea siempre nace dentro de un proyecto. */}
            {onCreate && (
              <div className="px-3 pt-2 flex-shrink-0">
                <QuickAddTask estado={primerEstado} onCreate={onCreate} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// --- Vista principal ---

// Serializacion de filtros ↔ URL. Las listas se almacenan como comma-separated
// (?asignados=1,5 / ?prioridades=urgente,alta). Sobrevive refresh y se puede
// compartir por link sin necesitar guardar la vista.
const parseList = (param) => (param ? param.split(',').filter(Boolean) : []);
const parseListInt = (param) => parseList(param).map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));

const filtersFromSearchParams = (searchParams) => ({
  busqueda: searchParams.get('q') || '',
  asignados: parseListInt(searchParams.get('asignados')),
  prioridades: parseList(searchParams.get('prioridades')),
  etiquetas: parseListInt(searchParams.get('etiquetas')),
  soloMias: searchParams.get('soloMias') === '1',
});

const filtersToSearchParams = (filters, prev) => {
  const next = new URLSearchParams(prev);
  // 'vistaId' es un parametro legacy que ya no se usa; lo limpiamos si estaba.
  next.delete('vistaId');
  const setOrDelete = (key, val) => {
    if (val) next.set(key, val);
    else next.delete(key);
  };
  setOrDelete('q', filters.busqueda?.trim());
  setOrDelete('asignados', (filters.asignados || []).join(','));
  setOrDelete('prioridades', (filters.prioridades || []).join(','));
  setOrDelete('etiquetas', (filters.etiquetas || []).join(','));
  setOrDelete('soloMias', filters.soloMias ? '1' : '');
  return next;
};

const ProyectoBoardView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    proyecto,
    tareas,
    refetchTareas,
    refetchProyecto,
    updateTareaLocal,
    addTareaLocal,
    color,
    // Vista general (/proyectos/vista-general): las columnas son las 4 categorias
    // fijas y cada tarea se mueve al estado real de su propio proyecto.
    esVistaGeneral = false,
    proyectosMap,
    resolverEstadoId,
  } = useOutletContext();

  const reduce = useReducedMotion();
  // Arrastre en curso: { tarea, rect (de la tarjeta al agarrarla), origen (punto
  // de agarre, eje del giro), zonaOrigen }.
  const [arrastre, setArrastre] = useState(null);
  // Zona sobre la que esta el puntero (id de sub-estado, o 'cat:x' en la vista general).
  const [zonaDestino, setZonaDestino] = useState(null);
  // Desde donde aterriza la tarjeta recien soltada (lo consume esa tarjeta al montar).
  const [aterrizaje, setAterrizaje] = useState(null);
  // Generacion del layoutId de cada tarjeta. Sube con cada arrastre para que la
  // tarjeta que aterriza no herede el recuerdo de framer de donde estaba al
  // agarrarla (el vuelo lo hace el aterrizaje, desde donde se solto).
  const [versiones, setVersiones] = useState({});
  const [slideOverTareaId, setSlideOverTareaId] = useState(null);

  // Puntero relativo al agarre: mueve la tarjeta flotante sin renders. Su
  // velocidad horizontal la inclina, con un resorte que la devuelve derecha
  // cuando la mano se detiene.
  const punteroX = useMotionValue(0);
  const punteroY = useMotionValue(0);
  const inicioPuntero = useRef(null);
  const velocidadX = useVelocity(punteroX);
  const inclinacion = useSpring(
    useTransform(velocidadX, [-1400, 0, 1400], [-6, 0, 6]),
    { stiffness: 300, damping: 22 }
  );
  // Initial state derivado de los query params: ?q=...&prioridades=urgente,alta&...
  // permite compartir/refrescar manteniendo la vista. Cambios subsiguientes
  // serializan de vuelta a la URL (effect mas abajo).
  const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));

  // Cuando los filtros locales cambian, reflejarlo en la URL. Comparacion con
  // la serializacion actual para evitar loops innecesarios.
  useEffect(() => {
    const next = filtersToSearchParams(filters, searchParams);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Tareas recién completadas (drop a categoría 'hecho') → trigger overlay de check verde
  // que aparece y desaparece tras ~900ms.
  const [recienCompletadas, setRecienCompletadas] = useState(new Set());

  // Seleccion multiple para bulk actions (mismo patron que Lista).
  const [seleccionados, setSeleccionados] = useState(new Set());
  const toggleSelect = useCallback((id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSeleccionados(new Set()), []);
  const hasAnySelection = seleccionados.size > 0;

  const tareasFiltradas = useMemo(
    () => applyFilters(tareas, filters, user?.empleado_id),
    [tareas, filters, user?.empleado_id]
  );

  // Categorias colapsadas: persistir por proyecto en localStorage.
  // v2: invalidamos las claves viejas porque cambiamos los defaults (antes
  // 'hecho' y 'cancelado' arrancaban colapsadas; ahora todas abiertas).
  const storageKey = `proyecto-board-collapsed-v2-${proyecto?.id ?? proyecto?.slug}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return CATEGORIAS.reduce((acc, c) => ({ ...acc, [c.key]: c.defaultCollapsed }), {});
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(collapsed)); } catch (e) {}
  }, [collapsed, storageKey]);

  const toggleCollapse = useCallback((cat) => {
    setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));
  }, []);

  // Sensors: activacion despues de 4px de movimiento (suficiente para distinguir click vs drag)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // Estados por categoria + tareas por estado (con filtros aplicados)
  const { estadosPorCategoria, tareasPorEstado } = useMemo(() => {
    const epc = {};
    const tpe = {};
    (proyecto?.estados || []).forEach((e) => {
      if (!epc[e.categoria]) epc[e.categoria] = [];
      epc[e.categoria].push(e);
      tpe[e.id] = [];
    });
    Object.keys(epc).forEach((cat) => {
      epc[cat].sort((a, b) => a.orden - b.orden);
    });
    tareasFiltradas.forEach((t) => {
      // En la vista general la columna la define la categoria (los estados son
      // virtuales, uno por categoria); en un proyecto, el sub-estado real.
      const key = esVistaGeneral ? `cat:${t.estado?.categoria}` : t.estado_id;
      if (!tpe[key]) tpe[key] = [];
      tpe[key].push(t);
    });
    Object.keys(tpe).forEach((eid) => {
      tpe[eid].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    return { estadosPorCategoria: epc, tareasPorEstado: tpe };
  }, [proyecto?.estados, tareasFiltradas, esVistaGeneral]);

  // --- Entradas: tarjetas que aparecen y antes no estaban a la vista ---
  //
  // Al cargar el tablero entran en cascada (columna por columna, de arriba
  // abajo); una tarea nueva o las que destapa un filtro, sin demora. Las que ya
  // estaban (y las que solo cambian de columna) no "entran": se deslizan.
  const visiblesPrevias = useRef(null);
  const entradas = useMemo(() => {
    const previas = visiblesPrevias.current;
    const esNueva = (id) => !previas || !previas.has(id);
    const cascada = tareasFiltradas.filter((t) => esNueva(t.id)).length >= 4;
    const mapa = new Map();
    CATEGORIAS.forEach((cat, ci) => {
      let fila = 0;
      (estadosPorCategoria[cat.key] || []).forEach((e) => {
        (tareasPorEstado[e.id] || []).forEach((t) => {
          if (esNueva(t.id)) mapa.set(t.id, cascada ? Math.min(0.42, ci * 0.07 + fila * 0.035) : 0);
          fila += 1;
        });
      });
    });
    return mapa;
  }, [estadosPorCategoria, tareasPorEstado, tareasFiltradas]);

  useEffect(() => {
    visiblesPrevias.current = new Set(tareasFiltradas.map((t) => t.id));
  }, [tareasFiltradas]);

  // --- Drag handlers ---

  // La tarjeta flotante sigue al puntero real (no al delta de dnd-kit, que suma
  // el scroll de la columna): va fija en la pantalla.
  useLayoutEffect(() => {
    if (!arrastre) return undefined;
    const mover = (e) => {
      const p = inicioPuntero.current;
      if (!p) return;
      punteroX.set(e.clientX - p.x);
      punteroY.set(e.clientY - p.y);
    };
    window.addEventListener('pointermove', mover);
    document.body.style.cursor = 'grabbing';
    return () => {
      window.removeEventListener('pointermove', mover);
      document.body.style.cursor = '';
    };
  }, [arrastre, punteroX, punteroY]);

  // El aterrizaje se consume al montar la tarjeta; despues se limpia para que
  // un remontaje posterior (un filtro, por ejemplo) no la haga volar de nuevo.
  useEffect(() => {
    if (!aterrizaje) return undefined;
    const t = setTimeout(() => setAterrizaje(null), 60);
    return () => clearTimeout(t);
  }, [aterrizaje]);

  const handleDragStart = (event) => {
    const tarea = event.active.data?.current?.tarea;
    if (!tarea) return;
    const ev = event.activatorEvent;
    const el = ev?.target?.closest?.('[data-tarjeta-id]');
    const r = el?.getBoundingClientRect();
    const px = ev?.clientX ?? (r ? r.left : 0);
    const py = ev?.clientY ?? (r ? r.top : 0);
    inicioPuntero.current = { x: px, y: py };
    punteroX.jump(0);
    punteroY.jump(0);
    inclinacion.jump(0);
    const zonaOrigen = zonaDeTarea(tarea, esVistaGeneral);
    setArrastre({
      tarea,
      rect: r ? { left: r.left, top: r.top, width: r.width, height: r.height } : null,
      origen: r ? `${px - r.left}px ${py - r.top}px` : '50% 50%',
      zonaOrigen,
    });
    setZonaDestino(zonaOrigen);
  };

  const handleDragOver = ({ over }) => {
    const data = over?.data?.current;
    setZonaDestino(data?.tipo === 'estado' ? data.estado.id : null);
  };

  // Sea cual sea el final (otra columna, la misma, cancelado o rechazado), la
  // tarjeta aterriza desde donde quedo la flotante.
  const soltar = (tareaId) => {
    if (arrastre?.rect && tareaId) {
      setAterrizaje({
        id: tareaId,
        left: arrastre.rect.left + punteroX.get(),
        top: arrastre.rect.top + punteroY.get(),
        rotate: reduce ? 0 : inclinacion.get(),
        scale: reduce ? 1 : ESCALA_ALZADA,
        origen: arrastre.origen,
      });
      setVersiones((v) => ({ ...v, [tareaId]: (v[tareaId] || 0) + 1 }));
    }
    setArrastre(null);
    setZonaDestino(null);
    inicioPuntero.current = null;
  };

  const handleDragCancel = (event) => {
    soltar(event.active.data?.current?.tarea?.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const tareaActiva = active.data?.current?.tarea;
    soltar(tareaActiva?.id);
    if (!over) return;
    if (!tareaActiva) return;

    // Destino: el sub-estado de la dropzone, o el estado de la tarea sobre la que se soltó.
    const overData = over.data?.current;
    const destino = overData?.tipo === 'estado'
      ? overData.estado
      : overData?.tipo === 'tarea'
      ? (overData.tarea.estado || (proyecto?.estados || []).find((e) => e.id === overData.tarea.estado_id))
      : null;

    if (!destino) return;

    // En la vista general el destino es una categoria: la tarea aterriza en el
    // estado de SU proyecto que pertenece a esa categoria.
    const nuevoEstado = esVistaGeneral
      ? resolverEstadoId?.(tareaActiva, destino.categoria)
      : destino;

    if (!nuevoEstado || nuevoEstado.id === tareaActiva.estado_id) return;
    const estadoDestinoId = nuevoEstado.id;

    // Permisos: mueven la tarea sus asignados y el lider del proyecto (owner).
    // Sin asignados la puede mover cualquier miembro. El backend es la fuente
    // de verdad; aqui solo evitamos el "salto" optimista. En la vista general
    // no tenemos el rol por proyecto, asi que decide el backend.
    if (!esVistaGeneral) {
      const soyLider = proyecto?.mi_rol === 'owner';
      const asignadosIds = (tareaActiva.asignados || []).map((a) => a.id);
      const esMia = asignadosIds.length === 0 || asignadosIds.includes(user?.empleado_id);
      if (!soyLider && !esMia) {
        toast.error('Solo sus asignados o el líder del proyecto pueden mover esta tarea');
        return;
      }
      if (
        !soyLider
        && tareaActiva.congelada
        && ['en_curso', 'hecho'].includes(nuevoEstado.categoria)
      ) {
        const codigos = (tareaActiva.bloqueadoras_pendientes || []).map((b) => b.codigo).join(', ');
        toast.error(`La tarea está en espera de ${codigos}`);
        return;
      }
    }

    // Actualizar optimistamente
    updateTareaLocal(tareaActiva.id, {
      estado_id: estadoDestinoId,
      estado: nuevoEstado,
    });

    // Si la tarea entró por primera vez a categoría 'hecho', disparar la celebración.
    // 1.5s da tiempo a: aterrizaje (~0.3s) + barrido y check (~0.6s) + onda (0.8s,
    // solapada); el exit se dispara después y dura 0.3s extra.
    if (nuevoEstado?.categoria === 'hecho' && tareaActiva.estado?.categoria !== 'hecho') {
      setRecienCompletadas((prev) => {
        const next = new Set(prev);
        next.add(tareaActiva.id);
        return next;
      });
      setTimeout(() => {
        setRecienCompletadas((prev) => {
          const next = new Set(prev);
          next.delete(tareaActiva.id);
          return next;
        });
      }, 1500);
    }

    // Persistir
    try {
      await axiosInstance.put(`/api/tareas/${tareaActiva.id}`, {
        estado_id: estadoDestinoId,
      });
    } catch (err) {
      console.error('Error moviendo tarea:', err);
      toast.error(err?.response?.data?.error || 'No se pudo mover la tarea');
      // Revertir
      updateTareaLocal(tareaActiva.id, {
        estado_id: tareaActiva.estado_id,
        estado: tareaActiva.estado,
      });
    }
  };

  // --- Crear tarea ---

  const handleCreate = useCallback(async ({ titulo, estado_id }) => {
    try {
      const { data } = await axiosInstance.post(`/api/proyectos/${proyecto.id}/tareas`, {
        titulo,
        estado_id,
      });
      addTareaLocal(data);
    } catch (err) {
      console.error('Error creando tarea:', err);
      throw err;
    }
  }, [proyecto?.id, addTareaLocal]);

  // --- Slide over ---

  const handleCardClick = useCallback((tarea) => {
    setSlideOverTareaId(tarea.id);
    navigate(`/proyectos/${slug}/tareas/${refTareaUrl(tarea, esVistaGeneral)}`, { replace: false });
  }, [navigate, slug, esVistaGeneral]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    navigate(`/proyectos/${slug}`, { replace: false });
  }, [navigate, slug]);

  // Orden visual de tareas (mismo que el board: categoria -> orden estado -> created_at)
  const tareasOrdenadas = useMemo(() => {
    const ORDER = { por_hacer: 0, en_curso: 1, hecho: 2, cancelado: 3 };
    return [...(tareas || [])].sort((a, b) => {
      const ca = ORDER[a.estado?.categoria ?? 'por_hacer'] ?? 99;
      const cb = ORDER[b.estado?.categoria ?? 'por_hacer'] ?? 99;
      if (ca !== cb) return ca - cb;
      const oa = a.estado?.orden ?? 0;
      const ob = b.estado?.orden ?? 0;
      if (oa !== ob) return oa - ob;
      return new Date(a.created_at) - new Date(b.created_at);
    });
  }, [tareas]);

  const handleNavigateSlideOver = useCallback((dir) => {
    if (!slideOverTareaId) return;
    const idx = tareasOrdenadas.findIndex((t) => t.id === slideOverTareaId);
    if (idx === -1) return;
    const nextIdx = dir === 'next' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= tareasOrdenadas.length) return;
    const next = tareasOrdenadas[nextIdx];
    setSlideOverTareaId(next.id);
    navigate(`/proyectos/${slug}/tareas/${refTareaUrl(next, esVistaGeneral)}`, { replace: false });
  }, [slideOverTareaId, tareasOrdenadas, navigate, slug, esVistaGeneral]);

  // Si la URL trae /tareas/:numero, abrir slide-over. Depende de location.pathname
  // ademas de tareas para que back/forward del navegador sincronicen.
  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/[^/]+\/tareas\/(\d+)/);
    if (match) {
      const ref = parseInt(match[1], 10);
      const tarea = tareaPorRefUrl(tareas, ref, esVistaGeneral);
      if (tarea) setSlideOverTareaId(tarea.id);
    } else {
      setSlideOverTareaId(null);
    }
  }, [tareas, location.pathname, esVistaGeneral]);

  // Hay filtros activos pero ninguna tarea matchea (con proyecto teniendo tareas).
  const hayFiltrosActivos = filters.asignados.length > 0
    || filters.prioridades.length > 0
    || filters.etiquetas.length > 0
    || filters.soloMias
    || filters.busqueda;
  const sinResultadosPorFiltros = hayFiltrosActivos
    && (tareas?.length || 0) > 0
    && tareasFiltradas.length === 0;

  const clearAllFilters = () => setFilters({
    asignados: [], prioridades: [], etiquetas: [], soloMias: false, busqueda: '',
  });

  // El slide-over necesita la config del proyecto REAL de la tarea (estados,
  // etiquetas, miembros, hitos). En la vista general el del contexto es sintetico.
  const proyectoDelSlideOver = useMemo(() => {
    if (!esVistaGeneral || !slideOverTareaId) return proyecto;
    const t = (tareas || []).find((x) => x.id === slideOverTareaId);
    return (t && proyectosMap?.[t.proyecto_id]) || proyecto;
  }, [esVistaGeneral, slideOverTareaId, tareas, proyectosMap, proyecto]);

  // Bulk actions (mismo enfoque que ProyectoListaView).
  const handleBulkArchivar = async () => {
    const ids = Array.from(seleccionados);
    await Promise.all(ids.map((id) => axiosInstance.post(`/api/tareas/${id}/archivar`)));
    clearSelection();
    refetchTareas();
  };

  const handleBulkEstado = async (estadoId) => {
    const ids = Array.from(seleccionados);
    // En la vista general el menu ofrece categorias (estados virtuales `cat:x`):
    // cada tarea se manda al estado equivalente de su propio proyecto.
    const destinoDe = (id) => {
      if (!esVistaGeneral) return estadoId;
      const t = (tareas || []).find((x) => x.id === id);
      return resolverEstadoId?.(t, String(estadoId).replace('cat:', ''))?.id || null;
    };
    await Promise.all(
      ids.map((id) => {
        const destino = destinoDe(id);
        return destino ? axiosInstance.put(`/api/tareas/${id}`, { estado_id: destino }) : null;
      }).filter(Boolean)
    );
    clearSelection();
    refetchTareas();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Bulk bar (aparece sobre BoardFilters cuando hay seleccion). Se despliega
          en vez de empujar el tablero de golpe; el overflow vuelve a visible al
          terminar para que el menu "Mover a..." no quede recortado. */}
      <AnimatePresence initial={false}>
        {hasAnySelection && (
          <motion.div
            key="bulk"
            initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
            animate={{ height: 'auto', opacity: 1, transitionEnd: { overflow: 'visible' } }}
            exit={{ height: 0, opacity: 0, overflow: 'hidden' }}
            transition={reduce ? { duration: 0 } : RESORTE_HUECO}
            className="relative z-30 flex-shrink-0"
          >
            <BulkBar
              count={seleccionados.size}
              estados={proyecto?.estados || []}
              onArchivar={handleBulkArchivar}
              onEstado={handleBulkEstado}
              onClear={clearSelection}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <BoardFilters
        proyecto={proyecto}
        filters={filters}
        setFilters={setFilters}
        userEmpleadoId={user?.empleado_id}
      />

      {sinResultadosPorFiltros && (
        <div className="flex items-center gap-3 px-6 py-2 bg-amber-50/40 border-b border-amber-200/60">
          <Sparkles size={13} className="text-amber-500 flex-shrink-0" strokeWidth={2} />
          <span className="text-xs text-amber-800 flex-1">
            Ninguna tarea coincide con los filtros aplicados.
          </span>
          <button
            onClick={clearAllFilters}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-100 px-2 py-0.5 rounded transition-colors"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={zonaBajoPuntero}
        measuring={MEDICION}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <motion.div
          layoutScroll
          id="board-scroll"
          className="flex-1 overflow-x-auto overflow-y-hidden"
        >
          <div className="inline-flex items-start gap-3 px-6 py-6 h-full min-w-full">
            {CATEGORIAS.map((cat) => {
              const estados = estadosPorCategoria[cat.key] || [];
              if (estados.length === 0) return null;
              return (
                <Column
                  key={cat.key}
                  categoria={cat}
                  estados={estados}
                  tareasPorEstado={tareasPorEstado}
                  collapsed={collapsed[cat.key]}
                  onToggleCollapse={() => toggleCollapse(cat.key)}
                  onCardClick={handleCardClick}
                  onCreate={esVistaGeneral ? null : handleCreate}
                  proyectoColor={color}
                  recienCompletadas={recienCompletadas}
                  seleccionados={seleccionados}
                  onToggleSelect={toggleSelect}
                  hasAnySelection={hasAnySelection}
                  arrastre={arrastre}
                  zonaDestino={zonaDestino}
                  aterrizaje={aterrizaje}
                  versiones={versiones}
                  entradas={entradas}
                  reduce={reduce}
                />
              );
            })}
          </div>
        </motion.div>
      </DndContext>

      <TarjetaFlotante
        arrastre={arrastre}
        x={punteroX}
        y={punteroY}
        inclinacion={inclinacion}
        proyectoColor={arrastre?.tarea?.proyecto_color || color}
        reduce={reduce}
      />

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={proyectoDelSlideOver}
        onProyectoChanged={refetchProyecto}
        onClose={handleCloseSlideOver}
        onUpdate={(patch) => {
          if (slideOverTareaId) updateTareaLocal(slideOverTareaId, patch);
        }}
        onArchive={() => {
          refetchTareas();
          handleCloseSlideOver();
        }}
        onDuplicate={(nueva) => {
          refetchTareas();
          setSlideOverTareaId(nueva.id);
        }}
        onNavigate={handleNavigateSlideOver}
      />
    </div>
  );
};

// --- Bulk actions bar ---
// Aparece encima de BoardFilters cuando hay tareas seleccionadas. Patron negro
// consistente con el bulk bar de la vista Lista.

const BulkBar = ({ count, estados, onArchivar, onEstado, onClear }) => {
  const [estadoOpen, setEstadoOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!estadoOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setEstadoOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [estadoOpen]);

  return (
    <div className="flex items-center gap-3 px-6 py-2 bg-slate-900 text-white text-xs">
      <span className="inline-flex items-center gap-1 tabular-nums font-medium">
        <NumeroRodante valor={count} /> {count === 1 ? 'seleccionada' : 'seleccionadas'}
      </span>
      <div className="h-3 w-px bg-white/30" />
      <button
        onClick={onArchivar}
        className="inline-flex items-center gap-1 px-1 hover:text-rose-300 transition-colors"
      >
        <Archive size={11} /> Archivar
      </button>
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setEstadoOpen((v) => !v)}
          className="inline-flex items-center gap-1 px-1 hover:text-cyan-300 transition-colors"
        >
          <Check size={11} /> Mover a...
        </button>
        {estadoOpen && (
          <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 py-1 z-30 max-h-72 overflow-y-auto">
            {estados.map((e) => (
              <button
                key={e.id}
                onClick={() => { onEstado(e.id); setEstadoOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
                {e.nombre}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1" />
      <button
        onClick={onClear}
        className="inline-flex items-center gap-1 hover:text-white/70 transition-colors"
        title="Limpiar selección"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default ProyectoBoardView;
